import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ThumbsDown, Flag, Pencil, Trash2, Send, Loader2, Pin, Check, X, MessageSquare, Reply, ChevronDown, Star } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { UserModal } from "@/components/UserModal";
import { toast } from "sonner";

const timeShort = (iso) => {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
};

const validHex = (hex) => !!hex && /^#[0-9a-fA-F]{6}$/.test(hex);

const WALL_TOPICS = [
  { key: "community", label: "Community Chat", desc: "Be respectful · 3 downvotes removes a post · profanity auto-filtered" },
  { key: "token-creation", label: "Token Creation", desc: "Talk tokenomics, supply, and launch mechanics" },
  { key: "token-listing", label: "Token Listing", desc: "Listings, exchanges, and market visibility" },
  { key: "token-lockers", label: "Token Lockers", desc: "Liquidity locks, vesting, and trust" },
  { key: "airdropper", label: "Airdropper", desc: "Airdrop campaigns, snapshots, and distribution" },
  { key: "autono-swap", label: "Autono Swap", desc: "Swaps, routing, and liquidity" },
];

const hexLum = (hex) => {
  if (!validHex(hex)) return 0;
  const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
};

export const ChatWall = ({ onRequestLogin, onRequestProfile }) => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");
  const [modalUser, setModalUser] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [topic, setTopic] = useState("community");
  const [topicOpen, setTopicOpen] = useState(false);
  const topicRef = useRef(null);
  const listRef = useRef(null);
  const nearBottom = useRef(true);

  const activeTopic = WALL_TOPICS.find((t) => t.key === topic) || WALL_TOPICS[0];

  const load = useCallback(() => {
    api
      .get("/wall", { params: { topic } })
      .then((r) => setPosts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [topic]);

  useEffect(() => {
    setLoading(true);
    setPosts([]);
    nearBottom.current = true;
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (topicRef.current && !topicRef.current.contains(e.target)) setTopicOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pinned = posts.filter((p) => p.pinned);
  const normal = posts.filter((p) => !p.pinned).slice().reverse(); // oldest -> newest

  useEffect(() => {
    if (loading) return;
    const el = listRef.current;
    if (el && nearBottom.current) el.scrollTop = el.scrollHeight;
  }, [posts, loading]);

  const onListScroll = () => {
    const el = listRef.current;
    if (!el) return;
    nearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 90;
  };

  const guard = () => {
    if (!user) return onRequestLogin?.(), false;
    if (!user.has_profile) return onRequestProfile?.(), false;
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim() || !guard()) return;
    setBusy(true);
    try {
      await api.post("/wall", { content, reply_to: replyTo?.id || null }, { params: { topic } });
      setContent("");
      setReplyTo(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/wall/${id}`, { content: editText });
      setEditId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const remove = async (id) => { try { await api.delete(`/wall/${id}`); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const downvote = async (id) => { if (!guard()) return; try { const { data } = await api.post(`/wall/${id}/downvote`); if (data.removed) toast.success("Post removed after 3 downvotes"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const report = async (id) => { if (!guard()) return; try { await api.post(`/wall/${id}/report`); toast.success("Reported to admin. This post is now locked."); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const togglePin = async (id) => { try { const { data } = await api.post(`/wall/${id}/pin`); toast.success(data.pinned ? "Post pinned" : "Post unpinned"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  const Bubble = ({ p, pinnedStyle }) => {
    const mine = p.mine;
    const c1 = validHex(p.author.color) ? p.author.color : null;
    const c2raw = validHex(p.author.color2) ? p.author.color2 : null;
    const custom = c1 || c2raw;
    const gA = c1 || c2raw;
    const gB = c2raw || c1;
    const avgLum = custom ? (hexLum(gA) + hexLum(gB)) / 2 : 0;
    const txt = custom ? (avgLum > 0.62 ? "#0a0a0a" : "#ffffff") : null;
    const needsScrim = custom && avgLum >= 0.35 && avgLum <= 0.82;
    const scrim = txt === "#ffffff" ? "rgba(0,0,0,0.28)" : "rgba(255,255,255,0.4)";
    const bubbleStyle = custom ? { backgroundImage: `linear-gradient(90deg, ${gA}, ${gB})`, color: txt } : undefined;
    const bubbleClass = custom
      ? `rounded-2xl ${mine ? "rounded-tr-md" : "rounded-tl-md"} border border-white/10`
      : pinnedStyle
      ? "rounded-2xl bg-cyan-500/10 border border-cyan-400/40 text-slate-100"
      : mine
      ? "rounded-2xl rounded-tr-md bg-gradient-to-br from-cyan-500 to-purple-600 text-black font-medium"
      : "rounded-2xl rounded-tl-md bg-white/10 text-slate-100 border border-white/5";
    return (
      <div data-testid={`wall-post-${p.id}`} className={`flex gap-2.5 ${mine ? "flex-row-reverse" : ""}`}>
        <button onClick={() => setModalUser(p.author.id)} className={`${mine ? "mt-0.5" : "mt-5"} shrink-0`}>
          <Avatar path={p.author.avatar_path} name={p.author.nickname} size="sm" online={p.author.online} />
        </button>
        <div className={`max-w-[78%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
          {!mine && (
            <button
              onClick={() => setModalUser(p.author.id)}
              data-testid={`wall-author-${p.id}`}
              className="mb-1 ml-1 inline-flex items-center gap-1 text-xs font-bold text-cyan-400 hover:underline"
            >
              {p.author.nickname}
              {p.author_is_admin && (
                <Star data-testid={`wall-admin-star-${p.id}`} className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
              )}
            </button>
          )}
          {mine && p.author_is_admin && (
            <span data-testid={`wall-admin-star-${p.id}`} className="mb-1 mr-1 inline-flex items-center gap-1 self-end text-[10px] font-bold uppercase tracking-wider text-yellow-400">
              <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" /> Admin
            </span>
          )}

          {editId === p.id ? (
            <div className="w-full">
              <textarea
                data-testid={`wall-edit-input-${p.id}`}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                rows={2}
                className="w-full rounded-2xl bg-white/5 border border-white/10 px-3 py-2.5 text-sm text-white focus:border-cyan-400/50 focus:outline-none resize-none"
              />
              <div className="mt-1.5 flex gap-2">
                <button data-testid={`wall-save-edit-${p.id}`} onClick={() => saveEdit(p.id)} className="btn-electric text-black font-bold text-xs px-3 py-1.5 rounded-lg inline-flex items-center gap-1"><Check className="w-3 h-3" /> Save</button>
                <button onClick={() => setEditId(null)} className="text-xs text-slate-400 px-2 hover:text-white inline-flex items-center gap-1"><X className="w-3 h-3" /> Cancel</button>
              </div>
            </div>
          ) : (
            <div
              className={`relative overflow-hidden px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words shadow-lg ${bubbleClass}`}
              style={bubbleStyle}
            >
              {needsScrim && <span aria-hidden className="absolute inset-0" style={{ background: scrim }} />}
              <span className="relative block">
              {p.reply_to && (
                <div className={`mb-2 rounded-lg border-l-2 pl-2 py-1 text-xs ${custom ? "border-current/50 bg-black/10" : mine ? "border-black/40 bg-black/10 text-black/70" : "border-cyan-400/60 bg-white/5 text-slate-400"}`} style={custom ? { opacity: 0.85 } : undefined}>
                  <span className="font-bold">↩ {p.reply_to.nickname}</span>
                  <span className="opacity-80">: {p.reply_to.snippet}</span>
                </div>
              )}
              {p.content}
              <span className="ml-2 inline-block align-bottom text-[10px] opacity-60" style={custom ? { color: txt } : (mine ? { color: "rgba(0,0,0,0.6)" } : { color: "#64748b" })}>
                {timeShort(p.created_at)}{p.edited && " · edited"}
              </span>
              </span>
            </div>
          )}

          {/* action row */}
          {editId !== p.id && (
            <div className={`mt-1 flex items-center gap-3 text-[11px] ${mine ? "justify-end" : "justify-start"}`}>
              {user && user.has_profile && (
                <button
                  data-testid={`wall-reply-${p.id}`}
                  onClick={() => { setReplyTo({ id: p.id, nickname: p.author.nickname, content: p.content }); document.querySelector("[data-testid='wall-composer-input']")?.focus({ preventScroll: true }); }}
                  className="inline-flex items-center gap-1 font-semibold text-slate-500 hover:text-cyan-400 transition-colors"
                >
                  <Reply className="w-3.5 h-3.5" /> reply
                </button>
              )}
              {!p.mine && !p.author_is_admin && (
                <button
                  data-testid={`wall-downvote-${p.id}`}
                  onClick={() => downvote(p.id)}
                  className={`inline-flex items-center gap-1 font-semibold transition-colors ${p.my_downvote ? "text-red-400" : "text-slate-500 hover:text-red-400"}`}
                >
                  <ThumbsDown className="w-3.5 h-3.5" /> {p.downvote_count}/3
                </button>
              )}
              {!p.mine && (
                <button data-testid={`wall-report-${p.id}`} onClick={() => report(p.id)} disabled={p.reported} className="inline-flex items-center gap-1 font-semibold text-slate-500 hover:text-amber-400 transition-colors disabled:opacity-40">
                  <Flag className="w-3.5 h-3.5" /> {p.reported ? "reported" : "report"}
                </button>
              )}
              {p.mine && !p.reported && (
                <>
                  <button data-testid={`wall-edit-${p.id}`} onClick={() => { setEditId(p.id); setEditText(p.content); }} className="inline-flex items-center gap-1 font-semibold text-slate-500 hover:text-cyan-400 transition-colors"><Pencil className="w-3.5 h-3.5" /> edit</button>
                  <button data-testid={`wall-delete-${p.id}`} onClick={() => remove(p.id)} className="inline-flex items-center gap-1 font-semibold text-slate-500 hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /> delete</button>
                </>
              )}
              {isAdmin && (
                <button data-testid={`wall-pin-${p.id}`} onClick={() => togglePin(p.id)} className={`inline-flex items-center gap-1 font-semibold transition-colors ${p.pinned ? "text-yellow-300" : "text-slate-500 hover:text-yellow-300"}`}>
                  <Pin className={`w-3.5 h-3.5 ${p.pinned ? "fill-yellow-300" : ""}`} /> {p.pinned ? "unpin" : "pin"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <section id="chat-wall" data-testid="chat-wall" className="relative px-5 sm:px-8 py-20 sm:py-28">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-cyan-400 font-semibold">
            <span className="w-6 h-px bg-cyan-400/60" /> Community Wall
          </span>
          <h2 className="font-heading font-extrabold tracking-tight mt-4 text-2xl sm:text-3xl lg:text-4xl text-gradient-chrome">
            The Autonomous Dex chat wall
          </h2>
        </div>

        {/* one big telegram-style chat card */}
        <div className="electric-card rounded-3xl bg-black/55 backdrop-blur-xl border border-white/10 flex flex-col overflow-hidden" style={{ height: "640px" }}>
          {/* header */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 bg-black/40">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center shrink-0">
              <MessageSquare className="w-5 h-5 text-black" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="relative inline-block" ref={topicRef}>
                <button
                  data-testid="wall-topics-button"
                  onClick={() => setTopicOpen((v) => !v)}
                  className="group flex items-center gap-1.5 font-heading font-bold text-white text-sm hover:text-cyan-300 transition-colors"
                >
                  {activeTopic.label}
                  <ChevronDown className={`w-4 h-4 text-cyan-400 transition-transform ${topicOpen ? "rotate-180" : ""}`} />
                  <span className="ml-1.5 rounded-full bg-cyan-400/15 border border-cyan-400/30 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-cyan-300">Topics</span>
                </button>
                {topicOpen && (
                  <div
                    data-testid="wall-topics-menu"
                    className="absolute left-0 z-30 mt-2 w-60 rounded-xl bg-black/95 backdrop-blur-xl border border-white/10 p-1.5 shadow-2xl"
                  >
                    {WALL_TOPICS.map((t) => (
                      <button
                        key={t.key}
                        data-testid={`wall-topic-${t.key}`}
                        onClick={() => { setTopic(t.key); setTopicOpen(false); setReplyTo(null); }}
                        className={`w-full text-left rounded-lg px-3 py-2 text-sm transition-colors ${topic === t.key ? "bg-cyan-400/15 text-cyan-200" : "text-slate-200 hover:bg-white/5"}`}
                      >
                        <span className="flex items-center justify-between gap-2">
                          <span className="font-semibold">{t.label}</span>
                          {topic === t.key && <Check className="w-3.5 h-3.5 text-cyan-300 shrink-0" />}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-500 truncate">{activeTopic.desc}</p>
            </div>
          </div>

          {/* pinned banners */}
          {pinned.map((p) => (
            <div key={p.id} data-testid={`wall-pinned-${p.id}`} className="px-5 py-3 border-b border-yellow-400/40 bg-yellow-400/10">
              <p className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-yellow-300 mb-1">
                <Pin className="w-3 h-3 fill-yellow-300" /> Pinned · {p.author.nickname}
              </p>
              <p className="text-sm text-yellow-50 break-words whitespace-pre-wrap">{p.content}</p>
              {isAdmin && (
                <button data-testid={`wall-unpin-${p.id}`} onClick={() => togglePin(p.id)} className="mt-1 text-[11px] text-yellow-200/70 hover:text-yellow-200">unpin</button>
              )}
            </div>
          ))}

          {/* messages */}
          <div ref={listRef} onScroll={onListScroll} className="flex-1 overflow-y-auto px-4 sm:px-5 py-5 space-y-5" data-testid="wall-messages">
            {loading ? (
              <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>
            ) : normal.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-slate-500 text-center px-6">
                <MessageSquare className="w-8 h-8 mb-3 opacity-40" />
                No messages in {activeTopic.label} yet. Be the first to start the conversation!
              </div>
            ) : (
              normal.map((p) => <Bubble key={p.id} p={p} />)
            )}
          </div>

          {/* composer */}
          <div className="border-t border-white/10 bg-black/40 p-3">
            {replyTo && user && user.has_profile && (
              <div data-testid="reply-preview" className="mb-2 flex items-center gap-2 rounded-lg border-l-2 border-cyan-400/60 bg-white/5 px-3 py-2">
                <Reply className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                <div className="flex-1 min-w-0 text-xs">
                  <span className="font-bold text-cyan-400">Replying to {replyTo.nickname}</span>
                  <span className="text-slate-500 block truncate">{replyTo.content}</span>
                </div>
                <button data-testid="reply-cancel" onClick={() => setReplyTo(null)} className="text-slate-500 hover:text-red-400 shrink-0">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            {user && user.has_profile ? (
              <form onSubmit={submit} className="flex items-end gap-2.5">
                <Avatar path={user.avatar_path} name={user.nickname} size="sm" />
                <textarea
                  data-testid="wall-composer-input"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(e); } }}
                  placeholder="Write a message…"
                  rows={1}
                  className="flex-1 max-h-28 rounded-2xl bg-white/5 border border-white/10 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-cyan-400/50 focus:outline-none resize-none"
                />
                <button type="submit" disabled={busy} data-testid="wall-post-button" className="btn-electric text-black rounded-full w-11 h-11 flex items-center justify-center shrink-0 disabled:opacity-60">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>
            ) : (
              <div className="flex items-center justify-between gap-4 flex-wrap px-2 py-1">
                <p className="text-sm text-slate-400">{user ? "Create a profile to chat." : "Log in and create a profile to chat."}</p>
                <button data-testid="wall-cta-button" onClick={() => (user ? navigate("/profile") : onRequestLogin?.())} className="btn-electric text-black font-bold text-sm px-5 py-2.5 rounded-full">
                  {user ? "Set up profile" : "Log in / Sign up"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <UserModal userId={modalUser} open={!!modalUser} onClose={() => setModalUser(null)} onMessage={(id) => navigate(`/profile?dm=${id}`)} />
    </section>
  );
};
